/**
 * CSVExportDialog Component
 * Export dialog for CSV data:
 * - Download as CSV file
 * - Include headers option
 * - Custom delimiter option
 * - Preview before export
 */

import { useState, useCallback, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Download,
  Copy,
  CheckCircle2,
  FileText,
} from 'lucide-react'
import type { TableHeader, TableRow } from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface CSVExportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  headers: TableHeader[]
  rows: TableRow[]
}

type Delimiter = ',' | '\t' | ';' | '|'

// ============================================
// Constants
// ============================================

const DELIMITERS: { value: Delimiter; label: string; extension: string }[] = [
  { value: ',', label: 'Comma (,)', extension: 'csv' },
  { value: '\t', label: 'Tab', extension: 'tsv' },
  { value: ';', label: 'Semicolon (;)', extension: 'csv' },
  { value: '|', label: 'Pipe (|)', extension: 'txt' },
]

// ============================================
// Helper Functions
// ============================================

/**
 * Escape a cell value for CSV
 */
function escapeCell(value: string, delimiter: Delimiter): string {
  const needsQuotes = value.includes(delimiter) ||
    value.includes('"') ||
    value.includes('\n') ||
    value.includes('\r')

  if (needsQuotes) {
    return `"${value.replace(/"/g, '""')}"`
  }

  return value
}

/**
 * Generate CSV content from table data
 */
function generateCSV(
  headers: TableHeader[],
  rows: TableRow[],
  delimiter: Delimiter,
  includeHeaders: boolean
): string {
  const lines: string[] = []

  // Add header row
  if (includeHeaders) {
    const headerLine = headers
      .map((h) => escapeCell(h.label, delimiter))
      .join(delimiter)
    lines.push(headerLine)
  }

  // Add data rows
  for (const row of rows) {
    const cells = headers.map((header) => {
      const cell = row.cells[header.key]
      return escapeCell(cell?.value || '', delimiter)
    })
    lines.push(cells.join(delimiter))
  }

  return lines.join('\n')
}

/**
 * Download content as a file
 */
function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// ============================================
// CSVExportDialog Component
// ============================================

export function CSVExportDialog({
  open,
  onOpenChange,
  headers,
  rows,
}: CSVExportDialogProps) {
  const [delimiter, setDelimiter] = useState<Delimiter>(',')
  const [includeHeaders, setIncludeHeaders] = useState(true)
  const [filename, setFilename] = useState('table-export')
  const [copied, setCopied] = useState(false)

  // Generate CSV content
  const csvContent = useMemo(
    () => generateCSV(headers, rows, delimiter, includeHeaders),
    [headers, rows, delimiter, includeHeaders]
  )

  // Get file extension
  const extension = DELIMITERS.find((d) => d.value === delimiter)?.extension || 'csv'

  // Handle download
  const handleDownload = useCallback(() => {
    const mimeType = delimiter === '\t' ? 'text/tab-separated-values' : 'text/csv'
    const fullFilename = `${filename}.${extension}`
    downloadFile(csvContent, fullFilename, mimeType)
  }, [csvContent, filename, extension, delimiter])

  // Handle copy to clipboard
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(csvContent)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [csvContent])

  // Reset state when dialog closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setDelimiter(',')
      setIncludeHeaders(true)
      setFilename('table-export')
      setCopied(false)
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export to CSV
          </DialogTitle>
          <DialogDescription>
            Export your table data to a CSV file or copy to clipboard.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Filename */}
          <div className="space-y-2">
            <Label htmlFor="export-filename">Filename</Label>
            <div className="flex gap-2">
              <Input
                id="export-filename"
                value={filename}
                onChange={(e) => setFilename(e.target.value.replace(/[^a-zA-Z0-9-_]/g, ''))}
                placeholder="table-export"
                className="flex-1"
              />
              <span className="flex items-center px-3 bg-neutral-100 dark:bg-neutral-800 rounded-lg text-sm text-neutral-500">
                .{extension}
              </span>
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center gap-6">
            <div className="space-y-1.5 flex-1">
              <Label>Delimiter</Label>
              <Select value={delimiter} onValueChange={(v) => setDelimiter(v as Delimiter)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DELIMITERS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3 pt-6">
              <Switch
                id="include-headers"
                checked={includeHeaders}
                onCheckedChange={setIncludeHeaders}
              />
              <Label htmlFor="include-headers" className="text-sm cursor-pointer">
                Include headers
              </Label>
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Preview
              </Label>
              <span className="text-xs text-neutral-500">
                {headers.length} columns, {rows.length} rows
              </span>
            </div>
            <div className="relative">
              <pre className="p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg border border-neutral-200 dark:border-neutral-700 text-xs font-mono overflow-auto max-h-[200px] whitespace-pre">
                {csvContent.split('\n').slice(0, 10).join('\n')}
                {csvContent.split('\n').length > 10 && (
                  <span className="text-neutral-400">
                    {'\n'}... {csvContent.split('\n').length - 10} more rows
                  </span>
                )}
              </pre>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
            <div className="flex-1">
              <p className="text-blue-700 dark:text-blue-300">
                <strong>{rows.length}</strong> rows,{' '}
                <strong>{headers.length}</strong> columns
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                {new Blob([csvContent]).size.toLocaleString()} bytes
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleCopy}
            className="gap-2"
          >
            {copied ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy to Clipboard
              </>
            )}
          </Button>
          <Button onClick={handleDownload} className="gap-2">
            <Download className="h-4 w-4" />
            Download {filename}.{extension}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CSVExportDialog
